import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore, api } from '../lib/auth';
import { useTranslation } from '../lib/i18n';
import { useDepos } from '../lib/useDepos';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { toast } from 'sonner';
import { format, getDaysInMonth, isWeekend, isSunday, getDay } from 'date-fns';
import { tr, enUS, nl } from 'date-fns/locale';
import {
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  X,
  Users,
  RefreshCw,
  Briefcase,
  Coffee,
  Heart,
  Clock,
  AlertCircle,
  PanelLeftClose,
  PanelLeftOpen,
  Shield,
  CheckCircle,
  XCircle,
  Filter,
  Search,
  UserX,
  Star,
  CalendarDays,
  AlertTriangle,
  ArrowRight,
  Plus,
  Coins,
  ExternalLink,
  FileText,
  History,
  RefreshCcw,
  ClipboardCheck,
  Trash2,
  Building2,
  MapPin
} from 'lucide-react';

// Turkish character normalization for search
const normalizeTr = (str) => {
  if (!str) return '';
  return str
    .replace(/İ/g, 'i').replace(/I/g, 'i')
    .replace(/Ş/g, 's').replace(/Ç/g, 'c').replace(/Ö/g, 'o').replace(/Ü/g, 'u').replace(/Ğ/g, 'g')
    .toLowerCase()
    .replace(/\u0307/g, '')
    .replace(/ı/g, 'i').replace(/ş/g, 's').replace(/ç/g, 'c').replace(/ö/g, 'o').replace(/ü/g, 'u').replace(/ğ/g, 'g');
};


// Finance type configuration
const FINANCE_TYPE_KEYS = {
  avans: { key: 'advance', color: 'bg-yellow-500' },
  pot_plus: { key: 'potPlus', color: 'bg-emerald-500' },
  pot_minus: { key: 'potMinus', color: 'bg-rose-500' },
  laadvergoeding: { key: 'laadvergoeding', color: 'bg-cyan-500' },
  leasing: { key: 'leasing', color: 'bg-purple-500' },
  diger: { key: 'other', color: 'bg-slate-50 dark:bg-slate-8000' }
};

// Status configuration with colors matching v1 (DailyStatus.jsx)
// Codes are language-specific
const STATUS_CONFIG = {
  WORKING: { 
    label: { tr: 'Çalıştı', en: 'Working', nl: 'Gewerkt', fr: 'Travaillé' },
    color: 'bg-emerald-500',
    borderColor: 'border-t-emerald-500',
    textColor: 'text-white',
    iconColor: 'text-emerald-500',
    code: { tr: 'Ç', en: 'W', nl: 'G', fr: 'T' },
    icon: CheckCircle
  },
  HALF_DAY: { 
    label: { tr: 'Yarım Gün', en: 'Half Day', nl: 'Halve Dag', fr: 'Demi-journée' },
    color: 'bg-amber-400',
    borderColor: 'border-t-amber-400',
    textColor: 'text-white',
    iconColor: 'text-amber-500',
    code: { tr: 'Y', en: 'H', nl: 'H', fr: 'D' },
    icon: Clock
  },
  SICK: { 
    label: { tr: 'Hasta', en: 'Sick', nl: 'Ziek', fr: 'Malade' },
    color: 'bg-red-500',
    borderColor: 'border-t-red-500',
    textColor: 'text-white',
    iconColor: 'text-red-500',
    code: { tr: 'H', en: 'S', nl: 'Z', fr: 'M' },
    icon: Heart
  },
  DOP: { 
    label: { tr: 'DOP', en: 'DOP', nl: 'DOP', fr: 'Repos' },
    color: 'bg-blue-500',
    borderColor: 'border-t-blue-500',
    textColor: 'text-white',
    iconColor: 'text-blue-500',
    code: { tr: 'D', en: 'D', nl: 'D', fr: 'R' },
    icon: Coffee
  },
  ON_LEAVE: { 
    label: { tr: 'İzinli', en: 'On Leave', nl: 'Verlof', fr: 'En congé' },
    color: 'bg-orange-500',
    borderColor: 'border-t-orange-500',
    textColor: 'text-white',
    iconColor: 'text-orange-500',
    code: { tr: 'İ', en: 'L', nl: 'V', fr: 'C' },
    icon: Briefcase
  },
  FEESTDAG: { 
    label: { tr: 'Tatil Günü', en: 'Holiday', nl: 'Feestdag', fr: 'Jour férié' },
    color: 'bg-purple-500',
    borderColor: 'border-t-purple-500',
    textColor: 'text-white',
    iconColor: 'text-purple-500',
    code: { tr: 'T', en: 'H', nl: 'F', fr: 'JF' },
    icon: Star
  },
  ZATERDAG: { 
    label: { tr: 'Cumartesi', en: 'Saturday', nl: 'Zaterdag', fr: 'Samedi' },
    color: 'bg-slate-600',
    borderColor: 'border-t-slate-600',
    textColor: 'text-white',
    iconColor: 'text-slate-600 dark:text-slate-300',
    code: { tr: 'C', en: 'Sa', nl: 'Za', fr: 'Sa' },
    icon: CalendarDays,
    customBgColor: '#475569'
  },
  NONE: { 
    label: { tr: 'Boş', en: 'Empty', nl: 'Leeg', fr: 'Vide' },
    color: 'bg-slate-200',
    borderColor: 'border-t-slate-400',
    textColor: 'text-slate-500 dark:text-slate-400',
    iconColor: 'text-slate-500 dark:text-slate-400',
    code: { tr: '-', en: '-', nl: '-', fr: '-' },
    icon: UserX
  }
};

const MONTHS = [
  { value: 0, label: { tr: 'Ocak', en: 'January', nl: 'Januari', fr: 'Janvier' } },
  { value: 1, label: { tr: 'Şubat', en: 'February', nl: 'Februari', fr: 'Février' } },
  { value: 2, label: { tr: 'Mart', en: 'March', nl: 'Maart', fr: 'Mars' } },
  { value: 3, label: { tr: 'Nisan', en: 'April', nl: 'April', fr: 'Avril' } },
  { value: 4, label: { tr: 'Mayıs', en: 'May', nl: 'Mei', fr: 'Mai' } },
  { value: 5, label: { tr: 'Haziran', en: 'June', nl: 'Juni', fr: 'Juin' } },
  { value: 6, label: { tr: 'Temmuz', en: 'July', nl: 'Juli', fr: 'Juillet' } },
  { value: 7, label: { tr: 'Ağustos', en: 'August', nl: 'Augustus', fr: 'Août' } },
  { value: 8, label: { tr: 'Eylül', en: 'September', nl: 'September', fr: 'Septembre' } },
  { value: 9, label: { tr: 'Ekim', en: 'October', nl: 'Oktober', fr: 'Octobre' } },
  { value: 10, label: { tr: 'Kasım', en: 'November', nl: 'November', fr: 'Novembre' } },
  { value: 11, label: { tr: 'Aralık', en: 'December', nl: 'December', fr: 'Décembre' } }
];

const getDateLocale = (lang) => {
  switch (lang) {
    case 'tr': return tr;
    case 'nl': return nl;
    default: return enUS;
  }
};

const DailyStatusV2 = () => {
  const { t, language } = useTranslation();
  const { depos: dbDepos } = useDepos();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const tableRef = useRef(null);
  const topScrollRef = useRef(null);
  const topScrollContentRef = useRef(null);
  const isSyncingScroll = useRef(false);
  
  // Check if user is admin (can edit statuses)
  const isAdmin = user?.role === 'SUPER_ADMIN' || user?.role === 'FULL_ADMIN' || user?.role === 'FIRM_ADMIN';
  
  // Check if user can view financial data
  const canViewFinancial = user?.role === 'SUPER_ADMIN' || user?.permissions?.can_view_financial === true;
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [firms, setFirms] = useState([]);
  const [selectedFirms, setSelectedFirms] = useState([]);
  const [selectedDepos, setSelectedDepos] = useState([]);
  const [firmPopoverOpen, setFirmPopoverOpen] = useState(false);
  const [depoPopoverOpen, setDepoPopoverOpen] = useState(false);
  // Soft-link: depo (Çalışma Durumu V2 listesi) → planning_depots karşılığı
  // (`planning_depot_id` alanı). Bu state popover'da küçük bir "PostNL · Oevel"
  // chip + dış-link ikonu göstermek için tutuluyor; veriyi backend'den çekiyor.
  const [planningDepotMap, setPlanningDepotMap] = useState({}); // planning_depot_id → { name, company }
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [pdRes, cRes] = await Promise.all([
          api.get('/planning/depots'),
          api.get('/planning/companies').catch(() => ({ data: { data: [] } })),
        ]);
        if (cancelled) return;
        const compMap = {};
        for (const c of (cRes.data?.data || [])) compMap[c.id] = c.name;
        const out = {};
        for (const pd of (pdRes.data?.data || [])) {
          out[pd.id] = { name: pd.name, company: compMap[pd.company_id] || '—' };
        }
        setPlanningDepotMap(out);
      } catch { /* ignore — popover gracefully degrades */ }
    })();
    return () => { cancelled = true; };
  }, []);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [statusData, setStatusData] = useState({});
  // Planlamadan gelen pazar çalışmaları — sadece gösterim, toplam/maaşa dahil değil.
  const [sundayWork, setSundayWork] = useState({});
  const [pendingChanges, setPendingChanges] = useState({});
  const [monthlyAdvances, setMonthlyAdvances] = useState({}); // {user_id: total_advance}
  
  // Finance dialog state
  const [financeDialog, setFinanceDialog] = useState({ open: false, userId: null, userName: '' });
  const [financeForm, setFinanceForm] = useState({
    entry_type: 'avans',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: ''
  });
  const [savingFinance, setSavingFinance] = useState(false);
  
  // Note dialog state
  const [noteDialog, setNoteDialog] = useState({ open: false, userId: null, userName: '' });
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [showPreviousNotes, setShowPreviousNotes] = useState(false);
  const [previousNotes, setPreviousNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  
  // Selection state
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [selectedDay, setSelectedDay] = useState(null);
  const [bulkDialog, setBulkDialog] = useState({ open: false, day: null });
  const [cellDialog, setCellDialog] = useState({ open: false, userId: null, day: null, externalFirmId: null });
  const [allFirms, setAllFirms] = useState([]); // All firms for external firm selection
  
  // Selected day for summary (defaults to today)
  const [summaryDay, setSummaryDay] = useState(new Date().getDate());
  
  // Status detail dialog - shows employees with specific status on selected day
  const [statusDetailDialog, setStatusDetailDialog] = useState({ open: false, status: null, employees: [] });
  
  // Past date confirmation dialog (for Super Admin only)
  const [pastDateConfirmDialog, setPastDateConfirmDialog] = useState({ 
    open: false, 
    action: null, // 'cell' or 'bulk'
    userId: null,
    day: null,
    date: null,
    status: null // for quick edit mode
  });
  
  // Confirmation dialog for overwriting existing status in quick edit mode
  const [overwriteConfirmDialog, setOverwriteConfirmDialog] = useState({
    open: false,
    userId: null,
    day: null,
    currentStatus: null,
    newStatus: null
  });
  
  // Check if Super Admin
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const canEditPast = user?.role === 'SUPER_ADMIN';
  
  // Employee filter - for comparing specific employees
  const [employeeFilter, setEmployeeFilter] = useState([]);
  
  // Sync leaves state
  const [syncDialog, setSyncDialog] = useState({ open: false, loading: false, data: null });
  const [syncing, setSyncing] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  
  // Quick edit mode - when a status is selected, clicking cells applies that status directly
  const [quickEditStatus, setQuickEditStatus] = useState(null);
  
  const dateLocale = getDateLocale(language);
  
  // Calculate days in selected month
  const daysInMonth = useMemo(() => {
    return getDaysInMonth(new Date(selectedYear, selectedMonth));
  }, [selectedYear, selectedMonth]);
  
  // Today's date info
  const today = new Date();
  const todayString = format(today, 'yyyy-MM-dd');
  const isCurrentMonth = selectedYear === today.getFullYear() && selectedMonth === today.getMonth();
  
  // Check if a date is in the past (before today)
  const isDateInPast = (dateStr) => {
    const date = new Date(dateStr);
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return date < todayStart;
  };
  
  // Check if a date is before employee's employment start date
  const isDateBeforeEmploymentStart = (dateStr, employee) => {
    if (!employee?.employment_start_date) return false;
    const date = new Date(dateStr);
    const startDate = new Date(employee.employment_start_date);
    // Compare dates only (ignore time)
    date.setHours(0, 0, 0, 0);
    startDate.setHours(0, 0, 0, 0);
    return date < startDate;
  };
  
  // Check if a date is after employee's employment end date
  const isDateAfterEmploymentEnd = (dateStr, employee) => {
    if (!employee?.employment_end_date) return false;
    const date = new Date(dateStr);
    const endDate = new Date(employee.employment_end_date);
    // Compare dates only (ignore time)
    date.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    return date > endDate;
  };
  
  // Generate array of days for the month
  const days = useMemo(() => {
    return Array.from({ length: daysInMonth }, (_, i) => {
      const date = new Date(selectedYear, selectedMonth, i + 1);
      const dayOfWeek = getDay(date);
      const isSun = dayOfWeek === 0;
      const isSat = dayOfWeek === 6;
      const dateStr = format(date, 'yyyy-MM-dd');
      return {
        day: i + 1,
        date: dateStr,
        dayName: format(date, 'EEE', { locale: dateLocale }),
        isWeekend: isSun || isSat,
        isSunday: isSun,
        isToday: dateStr === todayString
      };
    });
  }, [selectedYear, selectedMonth, daysInMonth, dateLocale, todayString]);

  // Scroll to today's column when page loads or month changes
  useEffect(() => {
    if (isCurrentMonth && !loading && tableRef.current) {
      const todayColumn = tableRef.current.querySelector('[data-today="true"]');
      if (todayColumn) {
        setTimeout(() => {
          todayColumn.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }, 300);
      }
    }
  }, [isCurrentMonth, loading]);

  // Sync top scrollbar width with table scroll width
  useEffect(() => {
    const updateScrollWidth = () => {
      const tableEl = tableRef.current;
      const contentEl = topScrollContentRef.current;
      
      if (tableEl && contentEl) {
        const scrollWidth = tableEl.scrollWidth;
        const clientWidth = tableEl.clientWidth;
        // Only show scrollbar if table is scrollable
        if (scrollWidth > clientWidth) {
          contentEl.style.width = scrollWidth + 'px';
        } else {
          contentEl.style.width = '100%';
        }
      }
    };

    // Skip if still loading
    if (loading) return;

    // Initial update after table is rendered
    const timeoutId = setTimeout(updateScrollWidth, 300);
    
    // Also update on window resize
    window.addEventListener('resize', updateScrollWidth);
    
    // Create a ResizeObserver to watch the table
    let resizeObserver;
    if (tableRef.current && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateScrollWidth);
      resizeObserver.observe(tableRef.current);
    }
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateScrollWidth);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [loading, users, days, statusData]);

  // Scroll synchronization between top scrollbar and main table
  useEffect(() => {
    const topScroll = topScrollRef.current;
    const mainTable = tableRef.current;
    
    if (!topScroll || !mainTable) return;

    const handleTopScroll = () => {
      if (isSyncingScroll.current) return;
      isSyncingScroll.current = true;
      mainTable.scrollLeft = topScroll.scrollLeft;
      requestAnimationFrame(() => {
        isSyncingScroll.current = false;
      });
    };

    const handleMainScroll = () => {
      if (isSyncingScroll.current) return;
      isSyncingScroll.current = true;
      topScroll.scrollLeft = mainTable.scrollLeft;
      requestAnimationFrame(() => {
        isSyncingScroll.current = false;
      });
    };

    topScroll.addEventListener('scroll', handleTopScroll);
    mainTable.addEventListener('scroll', handleMainScroll);

    return () => {
      topScroll.removeEventListener('scroll', handleTopScroll);
      mainTable.removeEventListener('scroll', handleMainScroll);
    };
  }, [loading]);

  useEffect(() => {
    fetchUsers();
    fetchAllFirms(); // Always fetch firms for external firm selection
    if (isAdmin) {
      fetchFirms();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  useEffect(() => {
    if (users.length > 0) {
      fetchStatusData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedMonth, users, selectedFirms]);

  const fetchAllFirms = async () => {
    try {
      const response = await api.get('/admin/firms');
      if (response.data.success) {
        setAllFirms(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch all firms', error);
    }
  };

  const fetchFirms = async () => {
    try {
      const response = await api.get('/admin/firms');
      if (response.data.success) {
        setFirms(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch firms', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users?status=ACTIVE');
      if (response.data.success) {
        // Include both EMPLOYEE and FIRM_ADMIN in the list (exclude SUPER_ADMIN)
        const usersToShow = (response.data.data || []).filter(u => 
          u.role !== 'SUPER_ADMIN'
        );
        setUsers(usersToShow);
      }
    } catch (error) {
      console.error('Failed to fetch users', error);
      toast.error(t('common.error'));
    }
  };

  const fetchStatusData = async () => {
    setLoading(true);
    try {
      const startDate = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-01`;
      const endDate = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;
      
      let url = `/daily-status/range?start_date=${startDate}&end_date=${endDate}`;
      if (isAdmin && selectedFirms.length === 1) {
        url += `&firm_id=${selectedFirms[0]}`;
      }
      
      const response = await api.get(url);
      api.get(`/planning/sunday-work?start_date=${startDate}&end_date=${endDate}`)
        .then((res) => {
          const map = {};
          (res.data?.data || []).forEach((s) => {
            const k = `${s.employee_id}_${s.date}`;
            map[k] = map[k] ? `${map[k]}, ${s.route_code || ''}` : (s.route_code || 'Planlama');
          });
          setSundayWork(map);
        })
        .catch(() => setSundayWork({}));
      if (response.data.success) {
        // Convert array to lookup object: { `${userId}_${date}`: { status, external_firm_id } }
        const dataMap = {};
        (response.data.data || []).forEach(item => {
          const key = `${item.user_id}_${item.date}`;
          dataMap[key] = {
            status: item.status,
            external_firm_id: item.external_firm_id || null
          };
        });
        setStatusData(dataMap);
        setPendingChanges({});
      }
    } catch (error) {
      console.error('Failed to fetch status data', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  // Fetch monthly advances for all employees
  const fetchMonthlyAdvances = async () => {
    try {
      let url = `/employee-profile/finance/monthly-advances?year=${selectedYear}&month=${selectedMonth + 1}`;
      if (isAdmin && selectedFirms.length === 1) {
        url += `&firm_id=${selectedFirms[0]}`;
      }
      
      const response = await api.get(url);
      if (response.data.success) {
        setMonthlyAdvances(response.data.data || {});
      }
    } catch (error) {
      console.error('Failed to fetch monthly advances', error);
      // Don't show error toast - this is optional data
    }
  };

  // Handle adding finance entry
  const handleAddFinanceEntry = async () => {
    if (!financeForm.entry_type || !financeForm.amount || !financeForm.date) {
      toast.error(t('employeeProfile.requiredFields') || 'Lütfen zorunlu alanları doldurun');
      return;
    }

    try {
      setSavingFinance(true);
      const response = await api.post(`/employee-profile/finance/${financeDialog.userId}`, {
        entry_type: financeForm.entry_type,
        amount: parseFloat(financeForm.amount),
        date: financeForm.date,
        description: financeForm.description
      });

      if (response.data.success) {
        toast.success(t('employeeProfile.profileSaved') || 'Kayıt eklendi');
        setFinanceDialog({ open: false, userId: null, userName: '' });
        setFinanceForm({ entry_type: 'avans', amount: '', date: new Date().toISOString().split('T')[0], description: '' });
        // Refresh advances
        fetchMonthlyAdvances();
      }
    } catch (error) {
      console.error('Error adding finance entry:', error);
      toast.error(t('employeeProfile.profileSaveError') || 'Kayıt eklenemedi');
    } finally {
      setSavingFinance(false);
    }
  };

  // Open finance dialog for a specific employee
  const openFinanceDialog = (employee) => {
    setFinanceForm({
      entry_type: 'avans',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      description: ''
    });
    setFinanceDialog({ open: true, userId: employee.id, userName: employee.full_name });
  };

  // Open note dialog for a specific employee
  const openNoteDialog = (employee) => {
    setNoteText('');
    setNoteDialog({ open: true, userId: employee.id, userName: employee.full_name });
  };

  // Handle adding note
  const handleAddNote = async () => {
    if (!noteText.trim() || !noteDialog.userId) return;
    
    try {
      setSavingNote(true);
      const response = await api.post('/loonbrief/employee-notes', {
        user_id: noteDialog.userId,
        note: noteText.trim()
      });
      if (response.data.success) {
        toast.success('Not eklendi');
        setNoteDialog({ open: false, userId: null, userName: '' });
        setNoteText('');
        setShowPreviousNotes(false);
        setPreviousNotes([]);
      }
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Not eklenemedi');
    } finally {
      setSavingNote(false);
    }
  };

  // Fetch previous notes for a user
  const fetchPreviousNotes = async (userId) => {
    if (!userId) return;
    
    setLoadingNotes(true);
    try {
      const response = await api.get(`/loonbrief/employee-notes/${userId}`);
      if (response.data.success) {
        setPreviousNotes(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching notes:', error);
      toast.error('Notlar yüklenemedi');
    } finally {
      setLoadingNotes(false);
    }
  };

  // Handle toggle show previous notes
  const handleTogglePreviousNotes = () => {
    if (!showPreviousNotes && noteDialog.userId) {
      fetchPreviousNotes(noteDialog.userId);
    }
    setShowPreviousNotes(!showPreviousNotes);
  };

  // Fetch advances when month/year/firm changes
  useEffect(() => {
    if (users.length > 0 && isAdmin) {
      fetchMonthlyAdvances();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedMonth, selectedFirms, users, isAdmin]);

  // Get filtered and sorted users based on selected firm
  // Sort: FIRM_ADMIN first, then EMPLOYEE, each group A-Z by name
  const filteredUsers = useMemo(() => {
    let result = users;
    
    // Filter by firm if admin selected specific firms
    if (isAdmin && selectedFirms.length > 0) {
      result = users.filter(u => selectedFirms.includes(u.firm_id));
    }
    
    // İşten ayrılmış / henüz başlamamış: o ayla çalışma dönemi kesişmiyorsa ve
    // o ay hiç kaydı yoksa listede gösterilmez (tekrar işe alınırsa 1 kayıt yeterli).
    const monthStart = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-01`;
    const monthEnd = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;
    const usersWithRecords = new Set(Object.entries(statusData)
      .filter(([, v]) => v?.status && v.status !== 'FEESTDAG')
      .map(([k]) => k.slice(0, k.lastIndexOf('_'))));
    result = result.filter((u) => {
      const end = (u.employment_end_date || '').slice(0, 10);
      const start = (u.employment_start_date || '').slice(0, 10);
      const outside = (end && end < monthStart) || (start && start > monthEnd);
      return !outside || usersWithRecords.has(u.id);
    });

    // Filter by depo
    if (selectedDepos.length > 0) {
      result = result.filter(u => {
        const userDepo = u.depo || null;
        return selectedDepos.some(d => d === '__none__' ? userDepo === null : d === userDepo);
      });
    }
    
    // Apply employee filter if set
    if (employeeFilter.length > 0) {
      result = result.filter(u => employeeFilter.includes(u.id));
    }
    
    // Apply search filter
    if (employeeSearch.trim()) {
      const q = normalizeTr(employeeSearch.trim());
      result = result.filter(u => normalizeTr(u.full_name || '').includes(q));
    }
    
    // Sort: group by depo when all depos selected, otherwise A-Z
    if (selectedDepos.length === 0) {
      return result.sort((a, b) => {
        const depoA = a.depo || '';
        const depoB = b.depo || '';
        // No depo first
        if (!depoA && depoB) return -1;
        if (depoA && !depoB) return 1;
        // Same depo group: sort by name
        if (depoA === depoB) return (a.full_name || '').localeCompare(b.full_name || '', 'tr');
        // Different depo: sort by depo name
        return depoA.localeCompare(depoB, 'tr');
      });
    }
    
    // Specific depo or no grouping needed: Admins first, then employees, alphabetically
    return result.sort((a, b) => {
      if (a.role === 'FIRM_ADMIN' && b.role !== 'FIRM_ADMIN') return -1;
      if (a.role !== 'FIRM_ADMIN' && b.role === 'FIRM_ADMIN') return 1;
      return (a.full_name || '').localeCompare(b.full_name || '', 'tr');
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, selectedFirms, selectedDepos, user?.role, employeeFilter, employeeSearch, statusData, selectedYear, selectedMonth, daysInMonth]);

  // All users for filter dropdown (not filtered by employeeFilter)
  const allUsersForFilter = useMemo(() => {
    let result = users;
    if (isAdmin && selectedFirms.length > 0) {
      result = users.filter(u => selectedFirms.includes(u.firm_id));
    }
    if (selectedDepos.length > 0) {
      result = result.filter(u => {
        const userDepo = u.depo || null;
        return selectedDepos.some(d => d === '__none__' ? userDepo === null : d === userDepo);
      });
    }
    return result.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '', 'tr'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, selectedFirms, selectedDepos, user?.role]);

  // Filtered users for search in dropdown (Turkish-insensitive)
  const searchedUsersForFilter = useMemo(() => {
    if (!employeeSearchTerm.trim()) {
      return allUsersForFilter;
    }
    const q = normalizeTr(employeeSearchTerm.trim());
    return allUsersForFilter.filter(u =>
      normalizeTr(u.full_name || '').includes(q) ||
      normalizeTr(u.email || '').includes(q)
    );
  }, [allUsersForFilter, employeeSearchTerm]);

  // Mobile: toggle employee list visibility
  const [showEmployeeList, setShowEmployeeList] = useState(true);

  // Calculate summary statistics for the selected summary day
  const daySummary = useMemo(() => {
    const summary = {
      WORKING: 0,
      HALF_DAY: 0,
      SICK: 0,
      DOP: 0,
      ON_LEAVE: 0,
      FEESTDAG: 0,
      ZATERDAG: 0,
      NONE: 0
    };
    
    // Ensure summaryDay is valid for current month
    const validDay = Math.min(summaryDay, daysInMonth);
    const dateStr = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(validDay).padStart(2, '0')}`;
    
    // Use allUsersForFilter (not filtered by employeeFilter) for accurate summary
    // But exclude employees who have terminated before this date
    const usersForSummary = allUsersForFilter.filter(emp => !isDateAfterEmploymentEnd(dateStr, emp));
    
    usersForSummary.forEach(emp => {
      const key = `${emp.id}_${dateStr}`;
      let status = 'NONE';
      if (pendingChanges[key] !== undefined) {
        status = pendingChanges[key];
      } else if (statusData[key]) {
        // statusData[key] can be an object { status, external_firm_id } or a string
        status = typeof statusData[key] === 'object' ? statusData[key].status : statusData[key];
      }
      if (summary[status] !== undefined) {
        summary[status]++;
      }
    });
    
    return { ...summary, date: dateStr, day: validDay };
  }, [allUsersForFilter, statusData, pendingChanges, summaryDay, selectedYear, selectedMonth, daysInMonth]);

  // Get employees with specific status on summary day
  const getEmployeesWithStatus = (status) => {
    const validDay = Math.min(summaryDay, daysInMonth);
    const dateStr = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(validDay).padStart(2, '0')}`;
    
    // Exclude employees who have terminated before this date
    return allUsersForFilter
      .filter(emp => !isDateAfterEmploymentEnd(dateStr, emp))
      .filter(emp => {
        const key = `${emp.id}_${dateStr}`;
        let empStatus = 'NONE';
        if (pendingChanges[key] !== undefined) {
          empStatus = pendingChanges[key];
        } else if (statusData[key]) {
          empStatus = typeof statusData[key] === 'object' ? statusData[key].status : statusData[key];
        }
        return empStatus === status;
      });
  };

  // Handle summary card click - show employees with that status
  const handleSummaryCardClick = (statusKey) => {
    const employees = getEmployeesWithStatus(statusKey);
    setStatusDetailDialog({ open: true, status: statusKey, employees });
  };

  // Toggle quick edit mode for a status
  const toggleQuickEditMode = (statusKey) => {
    if (quickEditStatus === statusKey) {
      setQuickEditStatus(null); // Deselect if already selected
    } else {
      setQuickEditStatus(statusKey); // Select this status
    }
  };

  // Helper functions for status display
  const getStatusColor = (statusKey) => {
    const config = STATUS_CONFIG[statusKey];
    if (!config) return 'bg-slate-200';
    return config.color;
  };

  const getStatusLabel = (statusKey) => {
    const config = STATUS_CONFIG[statusKey];
    if (!config) return statusKey;
    return config.label[language] || config.label.tr;
  };

  const getStatusEmoji = (statusKey) => {
    const config = STATUS_CONFIG[statusKey];
    if (!config) return '';
    return config.code[language] || config.code.tr;
  };

  // Get status for a cell
  // Get cell status - returns just status string for backward compatibility
  const getCellStatus = (userId, date) => {
    const key = `${userId}_${date}`;
    if (pendingChanges[key] !== undefined) {
      return typeof pendingChanges[key] === 'object' ? pendingChanges[key].status : pendingChanges[key];
    }
    const data = statusData[key];
    if (!data) return 'NONE';
    return typeof data === 'object' ? data.status : data;
  };

  // Get full cell data including external_firm_id
  const getCellData = (userId, date) => {
    const key = `${userId}_${date}`;
    if (pendingChanges[key] !== undefined) {
      if (typeof pendingChanges[key] === 'object') {
        return pendingChanges[key];
      }
      return { status: pendingChanges[key], external_firm_id: null };
    }
    const data = statusData[key];
    if (!data) return { status: 'NONE', external_firm_id: null };
    if (typeof data === 'object') return data;
    return { status: data, external_firm_id: null };
  };

  // Get firm label (first letter + Ç) for external firm display
  const getExternalFirmLabel = (firmId) => {
    if (!firmId) return null;
    const firm = allFirms.find(f => f.id === firmId);
    if (!firm) return null;
    // Get first letter of firm name and add Ç
    const firstLetter = firm.name.charAt(0).toUpperCase();
    return `${firstLetter}Ç`;
  };

  // Calculate bulk update preview - how many will be updated vs skipped
  const bulkUpdatePreview = useMemo(() => {
    if (!bulkDialog.open || !bulkDialog.day) {
      return { toUpdate: 0, toSkip: 0, total: 0 };
    }
    
    const targetUsers = selectedUsers.size > 0 
      ? filteredUsers.filter(u => selectedUsers.has(u.id))
      : filteredUsers;
    
    let toUpdate = 0;
    let toSkip = 0;
    
    targetUsers.forEach(u => {
      const currentStatus = getCellStatus(u.id, bulkDialog.day.date);
      if (!currentStatus || currentStatus === 'NONE') {
        toUpdate++;
      } else {
        toSkip++;
      }
    });
    
    return { toUpdate, toSkip, total: targetUsers.length };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkDialog.open, bulkDialog.day, selectedUsers, filteredUsers, statusData, pendingChanges]);

  // Handle single cell click - but not on Sundays
  // If quick edit mode is active, apply status directly
  // If clicking on a cell with same status, clear it (toggle behavior)
  const handleCellClick = async (userId, day) => {
    if (day.isSunday) return; // Don't allow editing on Sundays
    
    // Check if date is in the past
    const isPast = isDateInPast(day.date);
    
    // Non-Super Admin cannot edit past dates
    if (isPast && !canEditPast) {
      toast.error(t('dailyStatus.cannotEditPastDates') || 'Geçmiş tarihleri düzenleyemezsiniz');
      return;
    }
    
    // Super Admin editing past date - show confirmation dialog
    if (isPast && canEditPast) {
      if (quickEditStatus) {
        // Quick edit mode with past date
        setPastDateConfirmDialog({
          open: true,
          action: 'quickEdit',
          userId,
          day,
          date: day.date,
          status: quickEditStatus
        });
      } else {
        // Normal mode with past date
        setPastDateConfirmDialog({
          open: true,
          action: 'cell',
          userId,
          day: day.day,
          date: day.date,
          status: null
        });
      }
      return;
    }
    
    // If quick edit mode is active, apply status directly without modal
    if (quickEditStatus) {
      const currentStatus = getCellStatus(userId, day.date);
      
      // If cell already has a status (not NONE), show confirmation dialog
      if (currentStatus && currentStatus !== 'NONE' && currentStatus !== quickEditStatus) {
        setOverwriteConfirmDialog({
          open: true,
          userId,
          day,
          currentStatus,
          newStatus: quickEditStatus
        });
        return;
      }
      
      // Toggle behavior: if clicking on same status, clear it
      const newStatus = currentStatus === quickEditStatus ? 'NONE' : quickEditStatus;
      
      const update = {
        user_id: userId,
        date: day.date,
        status: newStatus
      };
      await saveStatusDirectly([update]);
      return;
    }
    
    // Normal mode - open modal with current external_firm_id if exists
    const cellData = getCellData(userId, day.date);
    setCellDialog({ 
      open: true, 
      userId, 
      day: day.day, 
      date: day.date,
      externalFirmId: cellData.external_firm_id || null
    });
  };

  // Handle day header click (bulk update for all users on that day) - but not on Sundays
  const handleDayHeaderClick = (day) => {
    // Also set summary day when clicking header
    setSummaryDay(day.day);
    if (day.isSunday) return; // Don't allow bulk edit on Sundays
    
    // Check if date is in the past
    const isPast = isDateInPast(day.date);
    
    // Non-Super Admin cannot edit past dates
    if (isPast && !canEditPast) {
      toast.error(t('dailyStatus.cannotEditPastDates') || 'Geçmiş tarihleri düzenleyemezsiniz');
      return;
    }
    
    // Super Admin editing past date - show confirmation dialog
    if (isPast && canEditPast) {
      setPastDateConfirmDialog({
        open: true,
        action: 'bulk',
        userId: null,
        day,
        date: day.date,
        status: null
      });
      return;
    }
    
    setBulkDialog({ open: true, day });
  };

  // Direct save to backend (auto-save)
  const saveStatusDirectly = async (updates) => {
    try {
      const response = await api.post('/daily-status/bulk-update', { updates });
      if (response.data.success) {
        // Update local state with full data object including external_firm_id
        const newData = {};
        updates.forEach(u => {
          newData[`${u.user_id}_${u.date}`] = {
            status: u.status,
            external_firm_id: u.external_firm_id || null
          };
        });
        setStatusData(prev => ({ ...prev, ...newData }));
        return true;
      } else {
        toast.error(response.data.message || t('common.error'));
        return false;
      }
    } catch (error) {
      console.error('Failed to save status', error);
      toast.error(t('common.error'));
      return false;
    }
  };

  // Update single cell status - auto save
  const updateCellStatus = async (status) => {
    const update = {
      user_id: cellDialog.userId,
      date: cellDialog.date,
      status,
      external_firm_id: cellDialog.externalFirmId || null
    };
    
    setCellDialog({ open: false, userId: null, day: null, externalFirmId: null });
    
    const success = await saveStatusDirectly([update]);
    if (success) {
      toast.success(t('dailyStatus.statusUpdated'));
    }
  };

  // Bulk update status for a day - auto save
  const bulkUpdateStatus = async (status) => {
    const targetUsers = selectedUsers.size > 0 
      ? filteredUsers.filter(u => selectedUsers.has(u.id))
      : filteredUsers;
    
    // Filter out users who already have a status set (not NONE)
    // This prevents overwriting existing statuses like approved leaves
    const usersToUpdate = targetUsers.filter(u => {
      const currentStatus = getCellStatus(u.id, bulkDialog.day.date);
      return !currentStatus || currentStatus === 'NONE';
    });
    
    const skippedCount = targetUsers.length - usersToUpdate.length;
    
    if (usersToUpdate.length === 0) {
      toast.info(t('dailyStatus.allUsersHaveStatus') || 'Tüm kullanıcıların zaten bir durumu var');
      setBulkDialog({ open: false, day: null });
      setSelectedUsers(new Set());
      return;
    }
    
    const updates = usersToUpdate.map(u => ({
      user_id: u.id,
      date: bulkDialog.day.date,
      status
    }));
    
    setBulkDialog({ open: false, day: null });
    setSelectedUsers(new Set());
    
    const success = await saveStatusDirectly(updates);
    if (success) {
      if (skippedCount > 0) {
        toast.success(
          `${usersToUpdate.length} ${t('dailyStatus.employeesUpdated')} (${skippedCount} ${t('dailyStatus.skippedExisting') || 'mevcut durum atlandı'})`
        );
      } else {
        toast.success(
          `${usersToUpdate.length} ${t('dailyStatus.employeesUpdated')}`
        );
      }
    }
  };

  // Handle past date confirmation (Super Admin only)
  const handlePastDateConfirm = async () => {
    const { action, userId, day, date, status } = pastDateConfirmDialog;
    
    setPastDateConfirmDialog({ open: false, action: null, userId: null, day: null, date: null, status: null });
    
    if (action === 'cell') {
      // Open normal cell dialog after confirmation
      const cellData = getCellData(userId, date);
      setCellDialog({ open: true, userId, day, date, externalFirmId: cellData.external_firm_id || null });
    } else if (action === 'bulk') {
      // Open bulk dialog after confirmation
      setBulkDialog({ open: true, day });
    } else if (action === 'quickEdit') {
      // Apply quick edit status after confirmation
      const currentStatus = getCellStatus(userId, date);
      const newStatus = currentStatus === status ? 'NONE' : status;
      
      const update = {
        user_id: userId,
        date: date,
        status: newStatus
      };
      await saveStatusDirectly([update]);
    }
  };

  // Handle overwrite confirmation for quick edit mode
  const handleOverwriteConfirm = async () => {
    const { userId, day, newStatus } = overwriteConfirmDialog;
    
    setOverwriteConfirmDialog({ open: false, userId: null, day: null, currentStatus: null, newStatus: null });
    
    const update = {
      user_id: userId,
      date: day.date,
      status: newStatus
    };
    await saveStatusDirectly([update]);
  };

  // Cancel overwrite
  const handleOverwriteCancel = () => {
    setOverwriteConfirmDialog({ open: false, userId: null, day: null, currentStatus: null, newStatus: null });
  };

  // Save all pending changes (kept for backward compatibility but not used)
  const saveChanges = async () => {
    if (Object.keys(pendingChanges).length === 0) {
      toast.info(t('dailyStatus.noChanges'));
      return;
    }

    setSaving(true);
    try {
      // Convert pendingChanges to array of updates
      const updates = Object.entries(pendingChanges).map(([key, status]) => {
        // Key format: userId_yyyy-MM-dd
        // Split by last underscore to handle UUIDs with underscores
        const lastUnderscoreIndex = key.lastIndexOf('_');
        const userId = key.substring(0, lastUnderscoreIndex);
        const date = key.substring(lastUnderscoreIndex + 1);
        return { user_id: userId, date, status };
      });

      console.log('Saving updates:', updates);
      const response = await api.post('/daily-status/bulk-update', { updates });
      
      if (response.data.success) {
        // Merge pending changes into statusData
        setStatusData(prev => ({ ...prev, ...pendingChanges }));
        setPendingChanges({});
        toast.success(t('dailyStatus.changesSaved'));
      } else {
        toast.error(response.data.message || t('common.error'));
      }
    } catch (error) {
      console.error('Failed to save changes', error);
      toast.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  // Toggle user selection
  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  // Select/deselect all users
  const toggleAllUsers = () => {
    if (selectedUsers.size === filteredUsers.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(filteredUsers.map(u => u.id)));
    }
  };

  // Navigate months
  const navigateMonth = (direction) => {
    if (direction === 'prev') {
      if (selectedMonth === 0) {
        setSelectedMonth(11);
        setSelectedYear(selectedYear - 1);
      } else {
        setSelectedMonth(selectedMonth - 1);
      }
    } else {
      if (selectedMonth === 11) {
        setSelectedMonth(0);
        setSelectedYear(selectedYear + 1);
      } else {
        setSelectedMonth(selectedMonth + 1);
      }
    }
  };

  const hasPendingChanges = Object.keys(pendingChanges).length > 0;

  // Sync leaves to daily status
  const handleSyncPreview = async () => {
    setSyncDialog({ open: true, loading: true, data: null });
    try {
      const params = selectedFirms.length === 1 ? `?firm_id=${selectedFirms[0]}` : '';
      const res = await api.get(`/daily-status/sync-leaves-preview${params}`);
      if (res.data.success) {
        setSyncDialog({ open: true, loading: false, data: res.data.data });
      }
    } catch (error) {
      toast.error('Senkronizasyon önizleme hatası');
      setSyncDialog({ open: false, loading: false, data: null });
    }
  };

  const handleSyncConfirm = async (userIds = []) => {
    setSyncing(true);
    try {
      const payload = { user_ids: userIds };
      if (selectedFirms.length === 1) payload.firm_id = selectedFirms[0];
      const res = await api.post('/daily-status/sync-leaves', payload);
      if (res.data.success) {
        toast.success(res.data.message);
        setSyncDialog({ open: false, loading: false, data: null });
        // Refresh data
        fetchStatusData();
      }
    } catch (error) {
      toast.error('Senkronizasyon hatası');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="h-full flex flex-col" data-testid="daily-status-v2">
      {/* Header */}
      <div className="flex-shrink-0 p-4 sm:p-6 border-b border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t('dailyStatus.title')}
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {t('dailyStatus.description')}
            </p>
          </div>
          {user?.role === 'SUPER_ADMIN' && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleSyncPreview}
              className="flex items-center gap-2 text-orange-600 border-orange-200 hover:bg-orange-50"
              data-testid="sync-leaves-btn"
            >
              <RefreshCcw className="h-4 w-4" />
              {t('dailyStatus.syncLeaves') || 'İzinleri Senkronize Et'}
            </Button>
          )}
        </div>

        {/* Month/Year Navigation & Filters */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mt-4">
          {/* Month Navigation */}
          <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-700 rounded-lg p-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigateMonth('prev')}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-2 px-2 min-w-[160px] justify-center">
              <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(Number(v))}>
                <SelectTrigger className="w-[100px] h-8 border-0 bg-transparent">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map(m => (
                    <SelectItem key={m.value} value={String(m.value)}>
                      {m.label[language] || m.label.tr}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
                <SelectTrigger className="w-[80px] h-8 border-0 bg-transparent">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
                    <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigateMonth('next')}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Firm Filter + Employee Filter + Refresh - All in same row on mobile */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Firm Filter (Admin with multiple firms) - Multi-select */}
            {isAdmin && firms.length > 1 && (
              <Popover open={firmPopoverOpen} onOpenChange={setFirmPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant={selectedFirms.length > 0 ? "default" : "outline"}
                    size="sm"
                    className="h-9 gap-1.5"
                    data-testid="firm-multi-filter"
                  >
                    <Building2 className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">
                      {selectedFirms.length === 0
                        ? (t('common.allFirms') || 'Tüm Firmalar')
                        : selectedFirms.length === 1
                          ? firms.find(f => f.id === selectedFirms[0])?.name || t('firm.title')
                          : `${selectedFirms.length} ${t('firm.title')}`}
                    </span>
                    {selectedFirms.length > 0 && (
                      <Badge variant="secondary" className="h-5 px-1.5 text-xs">{selectedFirms.length}</Badge>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-2" align="start">
                  <div className="space-y-1">
                    <div
                      className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer text-sm"
                      onClick={() => setSelectedFirms([])}
                    >
                      <Checkbox checked={selectedFirms.length === 0} />
                      <span className="font-medium">{t('common.allFirms') || 'Tüm Firmalar'}</span>
                    </div>
                    <div className="border-t my-1" />
                    {firms.map(firm => (
                      <div
                        key={firm.id}
                        className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer text-sm"
                        onClick={() => {
                          setSelectedFirms(prev =>
                            prev.includes(firm.id) ? prev.filter(id => id !== firm.id) : [...prev, firm.id]
                          );
                        }}
                      >
                        <Checkbox checked={selectedFirms.includes(firm.id)} />
                        <span>{firm.name}</span>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            )}

            {/* Depo Filter - Multi-select */}
            {isAdmin && (
              <Popover open={depoPopoverOpen} onOpenChange={setDepoPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant={selectedDepos.length > 0 ? "default" : "outline"}
                    size="sm"
                    className="h-9 gap-1.5"
                    data-testid="depo-multi-filter"
                  >
                    <MapPin className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">
                      {selectedDepos.length === 0
                        ? t('common.allDepos')
                        : selectedDepos.length === 1
                          ? (selectedDepos[0] === '__none__' ? t('common.noDepo') : selectedDepos[0])
                          : `${selectedDepos.length} Depo`}
                    </span>
                    {selectedDepos.length > 0 && (
                      <Badge variant="secondary" className="h-5 px-1.5 text-xs">{selectedDepos.length}</Badge>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-2" align="start">
                  <div className="space-y-1">
                    <div
                      className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer text-sm"
                      onClick={() => setSelectedDepos([])}
                    >
                      <Checkbox checked={selectedDepos.length === 0} />
                      <span className="font-medium">{t('common.allDepos')}</span>
                    </div>
                    <div className="border-t my-1" />
                    {[
                      { value: '__none__', label: t('common.noDepo') },
                      ...dbDepos.map(d => ({ value: d.value, label: d.label, planning_depot_id: d.planning_depot_id })),
                    ].map(depo => {
                      const linked = depo.planning_depot_id ? planningDepotMap[depo.planning_depot_id] : null;
                      return (
                        <div
                          key={depo.value}
                          className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer text-sm"
                          onClick={() => {
                            setSelectedDepos(prev =>
                              prev.includes(depo.value) ? prev.filter(v => v !== depo.value) : [...prev, depo.value]
                            );
                          }}
                        >
                          <Checkbox checked={selectedDepos.includes(depo.value)} />
                          <span className="flex-1">{depo.label}</span>
                          {linked && (
                            <>
                              <span
                                className="text-[9px] px-1 py-0 rounded bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
                                title={`Planning'de: ${linked.name} (${linked.company})`}
                              >
                                {linked.company}
                              </span>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); navigate('/dashboard/planning'); }}
                                className="text-emerald-500 hover:text-emerald-700"
                                title={`Planning'e git: ${linked.name} (${linked.company})`}
                              >
                                <ExternalLink className="h-3 w-3" />
                              </button>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </PopoverContent>
              </Popover>
            )}

            {/* Employee Filter */}
            <Popover open={showFilterDropdown} onOpenChange={setShowFilterDropdown}>
              <PopoverTrigger asChild>
                <Button 
                  variant={employeeFilter.length > 0 ? "default" : "outline"} 
                  size="icon"
                  className="h-9 w-9 sm:h-9 sm:w-auto sm:px-3"
                >
                  <Filter className="h-4 w-4" />
                  <span className="hidden sm:inline sm:ml-1.5">{t('common.filter')}</span>
                  {employeeFilter.length > 0 && (
                    <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs hidden sm:flex">
                      {employeeFilter.length}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-0" align="end">
                <div className="p-3 border-b">
                  <div className="flex items-center gap-2">
                    <Search className="h-4 w-4 text-slate-400" />
                    <Input
                      placeholder={t('common.search')}
                      className="h-8 border-0 p-0 focus-visible:ring-0"
                      value={employeeSearchTerm}
                      onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                    />
                    {employeeSearchTerm && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 w-6 p-0"
                        onClick={() => setEmployeeSearchTerm('')}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                  )}
                </div>
              </div>
              <div className="max-h-[250px] overflow-y-auto p-2">
                {searchedUsersForFilter.length === 0 ? (
                  <div className="text-center py-4 text-slate-500 dark:text-slate-400 text-sm">
                    {t('common.noResults')}
                  </div>
                ) : (
                  searchedUsersForFilter.map(emp => (
                    <div 
                      key={emp.id} 
                      className={cn(
                        "flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-100 dark:bg-slate-700",
                        employeeFilter.includes(emp.id) && "bg-blue-50"
                      )}
                      onClick={() => {
                        if (employeeFilter.includes(emp.id)) {
                          setEmployeeFilter(prev => prev.filter(id => id !== emp.id));
                        } else {
                          setEmployeeFilter(prev => [...prev, emp.id]);
                        }
                      }}
                    >
                      <Checkbox checked={employeeFilter.includes(emp.id)} />
                      <span className="text-sm truncate flex-1">{emp.full_name}</span>
                      {emp.role === 'FIRM_ADMIN' && (
                        <Badge variant="outline" className="text-[10px] px-1">Admin</Badge>
                      )}
                    </div>
                  ))
                )}
              </div>
              {employeeFilter.length > 0 && (
                <div className="p-2 border-t">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => setEmployeeFilter([])}
                  >
                    <X className="h-4 w-4 mr-1" />
                    {t('common.clearFilter')}
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>

            {/* Refresh Button */}
            <Button variant="outline" size="icon" className="h-9 w-9" onClick={fetchStatusData} disabled={loading}>
              <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
            </Button>
          </div>
        </div>

        {/* Active Filter Tags */}
        {employeeFilter.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {employeeFilter.map(empId => {
              const emp = allUsersForFilter.find(u => u.id === empId);
              return emp ? (
                <Badge 
                  key={empId} 
                  variant="secondary" 
                  className="flex items-center gap-1 pr-1 cursor-pointer hover:bg-slate-200"
                  onClick={() => setEmployeeFilter(prev => prev.filter(id => id !== empId))}
                >
                  <span className="truncate max-w-[100px]">{emp.full_name}</span>
                  <X className="h-3 w-3" />
                </Badge>
              ) : null;
            })}
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-6 px-2 text-xs text-red-600"
              onClick={() => setEmployeeFilter([])}
            >
              {t('common.clearAll')}
            </Button>
          </div>
        )}

        {/* Day Summary Cards */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                {daySummary.day} {MONTHS[selectedMonth]?.label[language] || MONTHS[selectedMonth]?.label.tr} {selectedYear}
              </span>
              <span className="text-xs text-slate-400">
                ({t('dailyStatus.daySummary')})
              </span>
            </div>
            {/* Mobile: Toggle employee list button - more prominent */}
            <Button 
              variant={showEmployeeList ? "default" : "outline"}
              size="sm"
              onClick={() => setShowEmployeeList(!showEmployeeList)}
              className="sm:hidden flex items-center gap-1.5 text-xs"
            >
              {showEmployeeList ? (
                <>
                  <PanelLeftClose className="h-3.5 w-3.5" />
                  <span>{t('dailyStatus.hideEmployees')}</span>
                </>
              ) : (
                <>
                  <PanelLeftOpen className="h-3.5 w-3.5" />
                  <span>{t('dailyStatus.showEmployees')}</span>
                </>
              )}
            </Button>
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-7 gap-1.5 sm:gap-2">
            {Object.entries(STATUS_CONFIG).filter(([key]) => key !== 'ZATERDAG').map(([key, config]) => {
              const Icon = config.icon;
              const count = daySummary[key] || 0;
              const isNone = key === 'NONE';
              return (
                <Card 
                  key={key} 
                  className={cn(
                    "border-t-3 cursor-pointer hover:shadow-md transition-shadow",
                    config.borderColor,
                    count > 0 && "hover:scale-105",
                    isNone && count > 0 && "bg-amber-50 dark:bg-amber-900/30" // Highlight if there are unassigned people
                  )}
                  onClick={() => handleSummaryCardClick(key)}
                >
                  <div className="p-1.5 sm:p-2 text-center">
                    <div className="flex items-center justify-center gap-0.5 sm:gap-1">
                      {Icon && <Icon className={cn("h-3 w-3 sm:h-4 sm:w-4", config.iconColor)} />}
                      <span className={cn("text-base sm:text-lg font-bold", config.iconColor)}>
                        {count}
                      </span>
                    </div>
                    <span className="text-[9px] sm:text-[10px] text-slate-600 dark:text-slate-300 font-medium leading-tight">
                      {config.label[language] || config.label.tr}
                    </span>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Quick Edit Mode - Inline hint (only when not active) */}
        {!quickEditStatus && (
          <div className="mt-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-medium text-slate-600 dark:text-slate-300 uppercase">
                {t('dailyStatus.quickEditMode')}
              </span>
            </div>
            <p className="text-xs text-slate-400 mb-3">
              {t('dailyStatus.clickStatusToActivate') || 'Hızlı işaretleme için bir durum seçin'}
            </p>
            {/* Status Buttons - Inline when not active */}
            <div className="flex flex-wrap gap-2">
              {Object.entries(STATUS_CONFIG).filter(([key]) => key !== 'NONE').map(([key, config]) => {
                const bgStyle = config.customBgColor ? { backgroundColor: config.customBgColor } : {};
                return (
                  <button
                    key={key}
                    onClick={() => toggleQuickEditMode(key)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 transition-all bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600 hover:border-slate-400 dark:border-slate-500 hover:scale-105"
                  >
                    <div 
                      className={cn(
                        "w-5 h-5 rounded text-xs flex items-center justify-center font-bold",
                        `${!config.customBgColor ? config.color : ''} ${config.textColor}`
                      )}
                      style={config.customBgColor ? bgStyle : {}}
                    >
                      {config.code[language] || config.code.tr}
                    </div>
                    <span className="text-xs font-medium text-slate-700 dark:text-slate-200">
                      {config.label[language] || config.label.tr}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Table Container */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Scrollbar */}
        <div 
          ref={topScrollRef}
          className="overflow-x-auto overflow-y-hidden flex-shrink-0"
          style={{ height: '12px' }}
        >
          <div ref={topScrollContentRef} style={{ height: '1px' }} />
        </div>
        {/* Main Table */}
        <div 
          className="flex-1 overflow-auto" 
          ref={tableRef}
        >
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        ) : filteredUsers.length === 0 && !employeeSearch ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-500 dark:text-slate-400">
            <Users className="h-12 w-12 mb-4 opacity-30" />
            <p>{selectedDepos.length > 0 || selectedFirms.length > 0 ? t('common.noFilterResults') : t('common.noData')}</p>
          </div>
        ) : (
          <table className="w-full border-collapse table-fixed">
            <thead className="sticky top-0 z-30">
              <tr>
                {/* Checkbox column - hidden on mobile */}
                {isAdmin && (
                  <th className="sticky left-0 z-40 bg-slate-100 dark:bg-slate-700 p-1 border-b border-r border-slate-200 dark:border-slate-600 w-6 min-w-[24px] max-w-[24px] hidden sm:table-cell shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]">
                    <Checkbox 
                      checked={selectedUsers.size === filteredUsers.length && filteredUsers.length > 0}
                      onCheckedChange={toggleAllUsers}
                      className="h-3.5 w-3.5"
                    />
                  </th>
                )}
                {/* Employee name column - hideable on mobile */}
                <th className={cn(
                  "sticky z-40 bg-slate-100 dark:bg-slate-700 p-1 border-b border-r border-slate-200 dark:border-slate-600 text-left shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]",
                  isAdmin ? "left-0 sm:left-6" : "left-0",
                  showEmployeeList ? "min-w-[120px] sm:min-w-[180px] w-[120px] sm:w-[180px]" : "hidden sm:table-cell sm:min-w-[180px] sm:w-[180px]"
                )}>
                  <input
                    type="text"
                    value={employeeSearch}
                    onChange={(e) => setEmployeeSearch(e.target.value)}
                    placeholder={t('admin.employee') || 'ÇALIŞAN'}
                    className="w-full bg-transparent text-xs font-semibold text-slate-700 dark:text-slate-200 uppercase placeholder:text-slate-500 placeholder:uppercase outline-none border-b border-transparent focus:border-slate-400 transition-colors py-1"
                    data-testid="employee-search-input"
                  />
                </th>
                {/* Day columns */}
                {days.map((day) => {
                  const isSelectedSummaryDay = day.day === summaryDay;
                  return (
                    <th 
                      key={day.day}
                      data-today={day.isToday ? "true" : undefined}
                      data-day={day.day}
                      onClick={() => {
                        setSummaryDay(day.day);
                        if (isAdmin && !day.isSunday) handleDayHeaderClick(day);
                      }}
                      className={cn(
                        "border-b border-r border-slate-200 dark:border-slate-600 transition-colors",
                        day.isSunday 
                          ? "p-0 min-w-[12px] w-[12px] cursor-not-allowed" 
                          : "p-0.5 min-w-[28px] sm:min-w-[32px] w-[28px] sm:w-[32px] cursor-pointer bg-slate-50 dark:bg-slate-800 dark:bg-slate-900",
                        isAdmin && !day.isSunday && "hover:bg-slate-200",
                        day.isToday && !day.isSunday && "!bg-blue-600 text-white ring-2 ring-blue-400 ring-inset",
                        !day.isToday && !day.isSunday && isSelectedSummaryDay && "!bg-indigo-100 ring-2 ring-indigo-400 ring-inset",
                        !day.isToday && !day.isSunday && !isSelectedSummaryDay && day.isWeekend && "!bg-amber-50 dark:bg-amber-900/30"
                      )}
                      style={day.isSunday ? { backgroundColor: '#64748b' } : {}}
                    >
                      {day.isSunday ? (
                        <div className="flex items-center justify-center h-full">
                          <span className="text-[7px] text-white font-bold" style={{ writingMode: 'vertical-rl' }}>
                            P
                          </span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center py-0.5">
                          <span className={cn(
                            "text-[8px] sm:text-[9px] font-medium leading-none",
                            day.isToday ? "text-blue-100" : isSelectedSummaryDay ? "text-indigo-600" : day.isWeekend ? "text-amber-600" : "text-slate-500 dark:text-slate-400"
                          )}>
                            {day.dayName}
                          </span>
                          <span className={cn(
                            "text-xs sm:text-sm font-bold leading-none",
                            day.isToday ? "text-white" : isSelectedSummaryDay ? "text-indigo-700" : day.isWeekend ? "text-amber-700" : "text-slate-700 dark:text-slate-200"
                          )}>
                            {day.day}
                          </span>
                        </div>
                      )}
                    </th>
                  );
                })}
                {/* Advance column - before Total - only if can view financial */}
                {canViewFinancial && (
                <th className="p-1 border-b border-slate-200 dark:border-slate-600 w-[50px] min-w-[50px] max-w-[50px] bg-amber-50 dark:bg-amber-900/30 border-l-2 border-l-amber-300">
                  <span className="text-[10px] font-semibold text-amber-700">
                    {t('employeeProfile.advance') || 'Avans'}
                  </span>
                </th>
                )}
                {/* Summary column */}
                <th className="p-1 border-b border-slate-200 dark:border-slate-600 w-[45px] min-w-[45px] max-w-[45px] bg-slate-100 dark:bg-slate-700">
                  <span className="text-[10px] font-semibold text-slate-700 dark:text-slate-200">
                    {t('common.total')}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 && employeeSearch && (
                <tr>
                  <td colSpan={999} className="text-center py-8 text-slate-500">
                    <Search className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">"{employeeSearch}" için sonuç bulunamadı</p>
                  </td>
                </tr>
              )}
              {filteredUsers.map((employee, idx) => {
                // Depo group header
                const prevEmployee = idx > 0 ? filteredUsers[idx - 1] : null;
                const currentDepo = employee.depo || null;
                const prevDepo = prevEmployee ? (prevEmployee.depo || null) : '__start__';
                const showDepoHeader = selectedDepos.length === 0 && currentDepo !== prevDepo;
                const totalCols = (isAdmin ? 1 : 0) + 1 + days.length + (canViewFinancial ? 1 : 0) + 1;

                // Calculate summary for this employee
                const summary = { WORKING: 0, HALF_DAY: 0, SICK: 0, DOP: 0, ON_LEAVE: 0, FEESTDAG: 0, ZATERDAG: 0 };
                days.forEach(day => {
                  const status = getCellStatus(employee.id, day.date);
                  if (summary[status] !== undefined) {
                    summary[status]++;
                  }
                });
                const totalWorked = summary.WORKING + (summary.HALF_DAY * 0.5) + summary.FEESTDAG + summary.ZATERDAG;
                const isEmployeeAdmin = employee.role === 'FIRM_ADMIN';
                const isSelected = selectedUsers.has(employee.id);
                const rowBgColor = isSelected 
                  ? "bg-blue-50 dark:bg-blue-900/30" 
                  : idx % 2 === 0 
                    ? "bg-white dark:bg-slate-800" 
                    : "bg-slate-50 dark:bg-slate-800 dark:bg-slate-900";
                const selectedRowStyle = isSelected ? "ring-2 ring-blue-400 ring-inset" : "";

                return (
                  <React.Fragment key={employee.id}>
                    {showDepoHeader && (
                      <tr className="bg-slate-200 dark:bg-slate-700">
                        <td colSpan={totalCols} className="px-3 py-1.5 text-xs font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider border-b-2 border-slate-300 dark:border-slate-500">
                          {currentDepo ? `${currentDepo}` : `— ${t('common.noDepo')}`}
                        </td>
                      </tr>
                    )}
                    <tr className={cn(rowBgColor, selectedRowStyle, "transition-colors duration-150")}>
                    {/* Checkbox - only for admins, hidden on mobile */}
                    {isAdmin && (
                      <td className={cn(
                        "sticky left-0 z-20 p-1 border-b border-r border-slate-200 dark:border-slate-600 w-6 min-w-[24px] max-w-[24px] hidden sm:table-cell shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]",
                        isSelected ? "bg-blue-50 dark:bg-blue-900/30" : rowBgColor
                      )}>
                        <Checkbox 
                          checked={isSelected}
                          onCheckedChange={() => toggleUserSelection(employee.id)}
                          className="h-3.5 w-3.5"
                        />
                      </td>
                    )}
                    {/* Employee name - hideable on mobile, sticky left */}
                    <td className={cn(
                      "sticky z-20 p-1.5 sm:p-2 border-b border-r border-slate-200 dark:border-slate-600 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]",
                      isAdmin ? "left-0 sm:left-6" : "left-0",
                      isSelected ? "bg-blue-50 dark:bg-blue-900/30" : rowBgColor,
                      !showEmployeeList && "hidden sm:table-cell"
                    )}>
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <div className={cn(
                          "w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center flex-shrink-0",
                          isEmployeeAdmin ? "bg-blue-100" : "bg-slate-200"
                        )}>
                          {isEmployeeAdmin ? (
                            <Shield className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-blue-600" />
                          ) : (
                            <span className="text-[10px] sm:text-xs font-medium text-slate-600 dark:text-slate-300">
                              {employee.full_name?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-col min-w-0" title={employee.full_name}>
                          <span className="text-[11px] sm:text-sm font-medium text-slate-800 dark:text-slate-100 truncate max-w-[70px] sm:max-w-[120px]" title={employee.full_name}>
                            {employee.full_name}
                          </span>
                          {isEmployeeAdmin && (
                            <span className="text-[9px] sm:text-[10px] text-blue-600 font-medium">Admin</span>
                          )}
                          {/* Quick action buttons */}
                          <div className="flex items-center gap-1 mt-0.5">
                            {canViewFinancial && (
                            <button
                              onClick={(e) => { e.stopPropagation(); openFinanceDialog(employee); }}
                              className="text-[9px] text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:bg-amber-900/30 px-1 py-0.5 rounded flex items-center gap-0.5"
                              title={t('employeeProfile.addFinanceEntry') || 'Finansal Kayıt Ekle'}
                            >
                              <Coins className="h-2.5 w-2.5" />
                              <span className="hidden sm:inline">+</span>
                            </button>
                            )}
                            <button
                              onClick={(e) => { e.stopPropagation(); openNoteDialog(employee); }}
                              className="text-[9px] text-purple-600 hover:text-purple-700 hover:bg-purple-50 px-1 py-0.5 rounded flex items-center gap-0.5"
                              title="Not Ekle"
                            >
                              <FileText className="h-2.5 w-2.5" />
                              <span className="hidden sm:inline">+</span>
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/employees/${employee.id}`); }}
                              className="text-[9px] text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-1 py-0.5 rounded flex items-center gap-0.5"
                              title={t('employeeProfile.viewProfile') || 'Profil'}
                            >
                              <ExternalLink className="h-2.5 w-2.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>
                    {/* Day cells */}
                    {days.map((day) => {
                      const status = getCellStatus(employee.id, day.date);
                      const cellData = getCellData(employee.id, day.date);
                      const config = STATUS_CONFIG[status] || STATUS_CONFIG.NONE;
                      const isPending = pendingChanges[`${employee.id}_${day.date}`] !== undefined;
                      const externalFirmLabel = getExternalFirmLabel(cellData.external_firm_id);
                      const isBeforeStart = isDateBeforeEmploymentStart(day.date, employee);
                      const isAfterEnd = isDateAfterEmploymentEnd(day.date, employee);
                      
                      // Sunday cells are disabled - narrow width
                      if (day.isSunday) {
                        const sunRoute = sundayWork[`${employee.id}_${day.date}`];
                        return (
                          <td 
                            key={day.day}
                            className="p-0 border-b border-r border-slate-400 dark:border-slate-500 cursor-not-allowed w-[12px]"
                            style={{ backgroundColor: '#64748b' }}
                            title={sunRoute ? `Pazar çalıştı (planlamadan): ${sunRoute}` : undefined}
                            data-testid={sunRoute ? `sunday-worked-${employee.id}-${day.date}` : undefined}
                          >
                            {sunRoute ? (
                              <div className="w-full h-5 sm:h-6 bg-emerald-500 flex items-center justify-center text-[8px] font-bold text-white leading-none">
                                {STATUS_CONFIG.WORKING.code[language] || 'Ç'}
                              </div>
                            ) : (
                              <div className="w-full h-5 sm:h-6" />
                            )}
                          </td>
                        );
                      }
                      
                      // Cells before employment start date - disabled and grayed out
                      if (isBeforeStart) {
                        return (
                          <td 
                            key={day.day}
                            className={cn(
                              "p-0.5 border-b border-r border-slate-300 dark:border-slate-600 cursor-not-allowed w-[28px] sm:w-[32px]",
                              day.isWeekend && "bg-slate-200 dark:bg-slate-700"
                            )}
                            style={{ backgroundColor: day.isWeekend ? undefined : '#e2e8f0' }}
                            title="İşe başlama tarihinden önce"
                          >
                            <div className="w-full h-5 sm:h-6 rounded flex items-center justify-center">
                              <span className="text-[8px] text-slate-400">-</span>
                            </div>
                          </td>
                        );
                      }
                      
                      // Cells after employment end date - disabled and grayed out (red tint)
                      if (isAfterEnd) {
                        return (
                          <td 
                            key={day.day}
                            className={cn(
                              "p-0.5 border-b border-r border-slate-300 dark:border-slate-600 cursor-not-allowed w-[28px] sm:w-[32px]",
                              day.isWeekend && "bg-red-100 dark:bg-red-900/30"
                            )}
                            style={{ backgroundColor: day.isWeekend ? undefined : '#fee2e2' }}
                            title="İşten ayrılma tarihinden sonra"
                          >
                            <div className="w-full h-5 sm:h-6 rounded flex items-center justify-center">
                              <span className="text-[8px] text-red-400">X</span>
                            </div>
                          </td>
                        );
                      }
                      
                      return (
                        <td 
                          key={day.day}
                          onClick={() => isAdmin && handleCellClick(employee.id, day)}
                          className={cn(
                            "p-0.5 border-b border-r border-slate-200 dark:border-slate-600 transition-all w-[28px] sm:w-[32px]",
                            isAdmin && "cursor-pointer hover:ring-2 hover:ring-blue-400 hover:ring-inset",
                            day.isToday && "!bg-blue-100/70 border-blue-300",
                            day.isWeekend && !day.isToday && "bg-amber-50 dark:bg-amber-900/30/50"
                          )}
                        >
                          <div 
                            className={cn(
                              "w-full h-5 sm:h-6 rounded flex items-center justify-center text-[9px] sm:text-[10px] font-bold transition-all",
                              !config.customBgColor && config.color,
                              config.textColor,
                              isPending && "ring-2 ring-amber-400",
                              day.isToday && status === 'NONE' && "ring-2 ring-blue-400"
                            )}
                            style={config.customBgColor ? { backgroundColor: config.customBgColor } : {}}
                            title={externalFirmLabel ? allFirms.find(f => f.id === cellData.external_firm_id)?.name : undefined}
                          >
                            {externalFirmLabel || (config.code[language] || config.code.tr)}
                          </div>
                        </td>
                      );
                    })}
                    {/* Advance cell - before Total (only if can view financial) */}
                    {canViewFinancial && (
                    <td className={cn(
                      "p-1 border-b border-slate-200 dark:border-slate-600 w-[55px] min-w-[55px] max-w-[55px] border-l-2 border-l-amber-300",
                      isSelected ? "bg-blue-50 dark:bg-blue-900/30" : "bg-amber-50 dark:bg-amber-900/30"
                    )}>
                      <div className="text-center">
                        {monthlyAdvances[employee.id] ? (
                          <span className="text-xs sm:text-sm font-bold text-amber-700">
                            {(() => {
                              const val = monthlyAdvances[employee.id];
                              const hasDecimal = val % 1 !== 0;
                              return hasDecimal 
                                ? val.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '€'
                                : val.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + '€';
                            })()}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-300">-</span>
                        )}
                      </div>
                    </td>
                    )}
                    {/* Summary cell */}
                    <td className={cn(
                      "p-1 border-b border-slate-200 dark:border-slate-600 w-[50px] min-w-[50px] max-w-[50px]",
                      isSelected ? "bg-blue-50 dark:bg-blue-900/30" : "bg-slate-50 dark:bg-slate-800 dark:bg-slate-900"
                    )}>
                      <div className="text-center">
                        <span className="text-sm sm:text-base font-bold text-emerald-600">{totalWorked}</span>
                      </div>
                    </td>
                  </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
        </div>
      </div>

      {/* Single Cell Dialog */}
      <Dialog open={cellDialog.open} onOpenChange={(open) => !open && setCellDialog({ open: false, userId: null, day: null })}>
        <DialogContent className="max-w-xs sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center">
              {cellDialog.day && `${cellDialog.day} ${MONTHS[selectedMonth]?.label[language] || MONTHS[selectedMonth]?.label.tr}`}
            </DialogTitle>
            <DialogDescription className="text-center">
              {filteredUsers.find(u => u.id === cellDialog.userId)?.full_name}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {/* Grid layout: WORKING spans 2 columns, others in pairs */}
            <div className="grid grid-cols-2 gap-2">
              {/* WORKING - spans 2 columns */}
              {(() => {
                const config = STATUS_CONFIG.WORKING;
                const Icon = config.icon;
                return (
                  <Button
                    variant="outline"
                    className={cn(
                      "col-span-2 h-14 flex flex-col items-center justify-center gap-1 hover:scale-105 transition-transform",
                      config.color, config.textColor, "border-0"
                    )}
                    onClick={() => updateCellStatus('WORKING')}
                  >
                    {Icon && <Icon className="h-4 w-4" />}
                    <span className="text-xs font-medium">{config.label[language] || config.label.tr}</span>
                  </Button>
                );
              })()}
              {/* Other statuses - 2 columns grid */}
              {Object.entries(STATUS_CONFIG).filter(([key]) => key !== 'NONE' && key !== 'WORKING').map(([key, config]) => {
                const Icon = config.icon;
                const bgStyle = config.customBgColor ? { backgroundColor: config.customBgColor } : {};
                return (
                  <Button
                    key={key}
                    variant="outline"
                    className={cn(
                      "h-14 flex flex-col items-center justify-center gap-1 hover:scale-105 transition-transform",
                      !config.customBgColor && config.color, 
                      config.textColor, 
                      "border-0"
                    )}
                    style={bgStyle}
                    onClick={() => updateCellStatus(key)}
                  >
                    {Icon && <Icon className="h-4 w-4" />}
                    <span className="text-xs font-medium">{config.label[language] || config.label.tr}</span>
                  </Button>
                );
              })}
            </div>
          </div>
          
          {/* External Firm Selection */}
          <div className="pt-2 border-t border-slate-200 dark:border-slate-600">
            <label className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-2 block">
              {t('dailyStatus.externalFirm') || 'Diğer Firma'} ({t('common.optional') || 'opsiyonel'})
            </label>
            <Select 
              value={cellDialog.externalFirmId || 'none'} 
              onValueChange={(val) => setCellDialog(prev => ({ ...prev, externalFirmId: val === 'none' ? null : val }))}
            >
              <SelectTrigger className="w-full h-9 text-xs">
                <SelectValue placeholder={t('dailyStatus.selectExternalFirm') || 'Firma seçin...'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  <span className="text-slate-400">- {t('common.none') || 'Yok'} -</span>
                </SelectItem>
                {allFirms
                  .filter(f => {
                    // Exclude the employee's own firm
                    const employee = filteredUsers.find(u => u.id === cellDialog.userId);
                    return f.id !== employee?.firm_id;
                  })
                  .map(f => (
                    <SelectItem key={f.id} value={f.id}>
                      <span className="font-medium">{f.name}</span>
                      <span className="text-xs text-slate-400 ml-2">({f.name.charAt(0).toUpperCase()}Ç)</span>
                    </SelectItem>
                  ))
                }
              </SelectContent>
            </Select>
            {cellDialog.externalFirmId && (
              <p className="text-[10px] text-emerald-600 mt-1">
                ✓ {t('dailyStatus.willShowAs') || 'Gösterim:'} <strong>{getExternalFirmLabel(cellDialog.externalFirmId)}</strong>
              </p>
            )}
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => updateCellStatus('NONE')}
            >
              <X className="h-4 w-4 mr-2" />
              {t('common.clear')}
            </Button>
            <Button 
              variant="ghost" 
              className="w-full"
              onClick={() => setCellDialog({ open: false, userId: null, day: null, externalFirmId: null })}
            >
              {t('common.cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Update Dialog */}
      <Dialog open={bulkDialog.open} onOpenChange={(open) => !open && setBulkDialog({ open: false, day: null })}>
        <DialogContent className="max-w-xs sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">
              {bulkDialog.day && `${bulkDialog.day.day} ${MONTHS[selectedMonth]?.label[language] || MONTHS[selectedMonth]?.label.tr}`}
            </DialogTitle>
            <DialogDescription className="text-center">
              {selectedUsers.size > 0 
                ? `${selectedUsers.size} ${t('dailyStatus.selectedEmployees')}`
                : `${filteredUsers.length} ${t('dailyStatus.allEmployees')}`
              }
            </DialogDescription>
          </DialogHeader>
          
          {/* Bulk Update Preview */}
          <div className="flex items-center justify-center gap-4 p-3 bg-slate-50 dark:bg-slate-800 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-600">
            <div className="text-center">
              <div className="text-lg font-bold text-emerald-600">{bulkUpdatePreview.toUpdate}</div>
              <div className="text-[10px] text-slate-500 dark:text-slate-400 uppercase font-medium">
                {t('dailyStatus.willBeUpdated') || 'Güncellenecek'}
              </div>
            </div>
            <div className="h-8 w-px bg-slate-300" />
            <div className="text-center">
              <div className="text-lg font-bold text-amber-600">{bulkUpdatePreview.toSkip}</div>
              <div className="text-[10px] text-slate-500 dark:text-slate-400 uppercase font-medium">
                {t('dailyStatus.willBeSkipped') || 'Atlanacak'}
              </div>
            </div>
          </div>
          
          {bulkUpdatePreview.toSkip > 0 && (
            <div className="flex items-center gap-2 p-2 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 rounded-lg text-amber-700 text-xs">
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
              <span>{t('dailyStatus.existingStatusesProtected') || 'Mevcut durumlar korunacak'}</span>
            </div>
          )}
          
          {bulkUpdatePreview.toUpdate === 0 && (
            <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-xs">
              <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
              <span>{t('dailyStatus.allUsersHaveStatus') || 'Tüm kullanıcıların zaten bir durumu var'}</span>
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-2 py-2">
            {/* WORKING - spans 2 columns */}
            {(() => {
              const config = STATUS_CONFIG.WORKING;
              const Icon = config.icon;
              return (
                <Button
                  variant="outline"
                  className={cn(
                    "col-span-2 h-16 flex flex-col items-center justify-center gap-1.5 hover:scale-105 transition-transform",
                    config.color, config.textColor, "border-0"
                  )}
                  onClick={() => bulkUpdateStatus('WORKING')}
                >
                  {Icon && <Icon className="h-5 w-5" />}
                  <span className="text-xs font-semibold">{config.label[language] || config.label.tr}</span>
                </Button>
              );
            })()}
            {/* Other statuses - 2 columns grid */}
            {Object.entries(STATUS_CONFIG).filter(([key]) => key !== 'NONE' && key !== 'WORKING').map(([key, config]) => {
              const Icon = config.icon;
              const bgStyle = config.customBgColor ? { backgroundColor: config.customBgColor } : {};
              return (
                <Button
                  key={key}
                  variant="outline"
                  className={cn(
                    "h-16 flex flex-col items-center justify-center gap-1.5 hover:scale-105 transition-transform",
                    !config.customBgColor && config.color, 
                    config.textColor, 
                    "border-0"
                  )}
                  style={bgStyle}
                  onClick={() => bulkUpdateStatus(key)}
                >
                  {Icon && <Icon className="h-5 w-5" />}
                  <span className="text-xs font-semibold">{config.label[language] || config.label.tr}</span>
                </Button>
              );
            })}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => bulkUpdateStatus('NONE')}
            >
              <X className="h-4 w-4 mr-2" />
              {t('common.clearAll')}
            </Button>
            <Button 
              variant="ghost" 
              className="w-full"
              onClick={() => setBulkDialog({ open: false, day: null })}
            >
              {t('common.cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Detail Dialog - shows employees with specific status */}
      <Dialog open={statusDetailDialog.open} onOpenChange={(open) => !open && setStatusDetailDialog({ open: false, status: null, employees: [] })}>
        <DialogContent className="max-w-xs sm:max-w-md max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {statusDetailDialog.status && STATUS_CONFIG[statusDetailDialog.status] && (() => {
                const StatusIcon = STATUS_CONFIG[statusDetailDialog.status].icon;
                return (
                  <>
                    <div className={cn(
                      "w-8 h-8 rounded flex items-center justify-center",
                      STATUS_CONFIG[statusDetailDialog.status].color
                    )}>
                      {StatusIcon && <StatusIcon className="h-4 w-4 text-white" />}
                    </div>
                    <span>
                      {STATUS_CONFIG[statusDetailDialog.status].label[language] || STATUS_CONFIG[statusDetailDialog.status].label.tr}
                    </span>
                    <Badge variant="secondary" className="ml-auto">
                      {statusDetailDialog.employees.length}
                    </Badge>
                  </>
                );
              })()}
            </DialogTitle>
            <DialogDescription>
              {daySummary.day} {MONTHS[selectedMonth]?.label[language] || MONTHS[selectedMonth]?.label.tr} {selectedYear}
            </DialogDescription>
          </DialogHeader>
          
          <div className="overflow-y-auto max-h-[50vh] -mx-6 px-6">
            {statusDetailDialog.employees.length === 0 ? (
              <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                <Users className="h-12 w-12 mx-auto mb-2 opacity-30" />
                <p>{t('common.noData') }</p>
              </div>
            ) : (
              <div className="space-y-2">
                {statusDetailDialog.employees.map(emp => (
                  <div 
                    key={emp.id} 
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-800 dark:bg-slate-900 cursor-pointer"
                    onClick={() => {
                      // Add to filter and close dialog
                      if (!employeeFilter.includes(emp.id)) {
                        setEmployeeFilter(prev => [...prev, emp.id]);
                      }
                      setStatusDetailDialog({ open: false, status: null, employees: [] });
                    }}
                  >
                    <div className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
                      emp.role === 'FIRM_ADMIN' ? "bg-blue-100" : "bg-slate-200"
                    )}>
                      {emp.role === 'FIRM_ADMIN' ? (
                        <Shield className="h-4 w-4 text-blue-600" />
                      ) : (
                        <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
                          {emp.full_name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{emp.full_name}</p>
                      {emp.role === 'FIRM_ADMIN' && (
                        <p className="text-xs text-blue-600">Admin</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="ghost" 
              className="w-full"
              onClick={() => setStatusDetailDialog({ open: false, status: null, employees: [] })}
            >
              {t('common.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Floating Quick Edit Bar - Shows when a status is selected */}
      {quickEditStatus && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-600 shadow-[0_-4px_20px_rgba(0,0,0,0.15)] safe-area-bottom">
          <div className="max-w-screen-2xl mx-auto px-3 sm:px-4 py-3">
            {/* Mobile: Compact view */}
            <div className="flex items-center justify-between gap-2 sm:hidden">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div 
                  className={cn(
                    "w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm flex-shrink-0",
                    !STATUS_CONFIG[quickEditStatus].customBgColor && STATUS_CONFIG[quickEditStatus].color,
                    STATUS_CONFIG[quickEditStatus].textColor
                  )}
                  style={STATUS_CONFIG[quickEditStatus].customBgColor ? { backgroundColor: STATUS_CONFIG[quickEditStatus].customBgColor } : {}}
                >
                  {STATUS_CONFIG[quickEditStatus].code[language] || STATUS_CONFIG[quickEditStatus].code.tr}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                    {STATUS_CONFIG[quickEditStatus].label[language] || STATUS_CONFIG[quickEditStatus].label.tr}
                  </p>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">
                    {t('dailyStatus.tapCellToApply') || 'Hücreye tıkla'}
                  </p>
                </div>
              </div>
              {/* Other status buttons - scrollable */}
              <div className="flex items-center gap-1.5 overflow-x-auto flex-shrink-0 max-w-[45%] pb-1 scrollbar-hide">
                {Object.entries(STATUS_CONFIG).filter(([key]) => key !== 'NONE' && key !== quickEditStatus).map(([key, config]) => (
                  <button
                    key={key}
                    onClick={() => toggleQuickEditMode(key)}
                    className={cn(
                      "w-7 h-7 rounded-md flex items-center justify-center font-bold text-xs flex-shrink-0 transition-all",
                      !config.customBgColor && config.color,
                      config.textColor,
                      "opacity-60 hover:opacity-100"
                    )}
                    style={config.customBgColor ? { backgroundColor: config.customBgColor } : {}}
                  >
                    {config.code[language] || config.code.tr}
                  </button>
                ))}
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-slate-500 dark:text-slate-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0"
                onClick={() => setQuickEditStatus(null)}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* Desktop: Full view */}
            <div className="hidden sm:flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div 
                  className={cn(
                    "w-10 h-10 rounded-lg flex items-center justify-center font-bold",
                    !STATUS_CONFIG[quickEditStatus].customBgColor && STATUS_CONFIG[quickEditStatus].color,
                    STATUS_CONFIG[quickEditStatus].textColor
                  )}
                  style={STATUS_CONFIG[quickEditStatus].customBgColor ? { backgroundColor: STATUS_CONFIG[quickEditStatus].customBgColor } : {}}
                >
                  {STATUS_CONFIG[quickEditStatus].code[language] || STATUS_CONFIG[quickEditStatus].code.tr}
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                    {t('dailyStatus.quickEditMode')}: {STATUS_CONFIG[quickEditStatus].label[language] || STATUS_CONFIG[quickEditStatus].label.tr}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {t('dailyStatus.clickCellToApply') || 'Durum uygulamak için hücreye tıklayın • Tekrar tıklayınca temizler'}
                  </p>
                </div>
              </div>
              
              {/* Quick switch to other statuses */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400 mr-1">{t('dailyStatus.switchTo') || 'Değiştir:'}</span>
                {Object.entries(STATUS_CONFIG).filter(([key]) => key !== 'NONE' && key !== quickEditStatus).map(([key, config]) => (
                  <button
                    key={key}
                    onClick={() => toggleQuickEditMode(key)}
                    className={cn(
                      "w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm transition-all hover:scale-110",
                      !config.customBgColor && config.color,
                      config.textColor,
                      "opacity-70 hover:opacity-100"
                    )}
                    style={config.customBgColor ? { backgroundColor: config.customBgColor } : {}}
                    title={config.label[language] || config.label.tr}
                  >
                    {config.code[language] || config.code.tr}
                  </button>
                ))}
              </div>

              <Button
                variant="outline"
                size="sm"
                className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                onClick={() => setQuickEditStatus(null)}
              >
                <X className="h-4 w-4 mr-1" />
                {t('dailyStatus.exitQuickEdit') || 'Çıkış'}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Past Date Confirmation Dialog - Super Admin Only */}
      <Dialog open={pastDateConfirmDialog.open} onOpenChange={(v) => !v && setPastDateConfirmDialog({ open: false, action: null, userId: null, day: null, date: null, status: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertCircle className="h-5 w-5" />
              {t('dailyStatus.pastDateWarning') || 'Geçmiş Tarih Uyarısı'}
            </DialogTitle>
            <DialogDescription className="space-y-3 pt-2">
              <div className="p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 rounded-lg">
                <p className="text-amber-800 font-medium">
                  {pastDateConfirmDialog.date && format(new Date(pastDateConfirmDialog.date), 'd MMMM yyyy, EEEE', { locale: dateLocale })}
                </p>
              </div>
              <p className="text-slate-600 dark:text-slate-300">
                {t('dailyStatus.pastDateConfirmMessage') || 'Geçmiş bir tarihteki çalışan durumunu değiştirmek üzeresiniz. Bu işlem kayıtları etkileyecektir.'}
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {t('dailyStatus.pastDateConfirmNote') || 'Bu önemli bir işlem olduğundan, devam etmek istediğinizden emin olun.'}
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setPastDateConfirmDialog({ open: false, action: null, userId: null, day: null, date: null, status: null })}
            >
              {t('common.cancel') || 'İptal'}
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={handlePastDateConfirm}
            >
              <Shield className="h-4 w-4 mr-2" />
              {t('dailyStatus.confirmPastEdit') || 'Evet, Düzenle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Overwrite Confirmation Dialog for Quick Edit Mode */}
      <Dialog open={overwriteConfirmDialog.open} onOpenChange={(v) => !v && handleOverwriteCancel()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              {t('dailyStatus.overwriteConfirmTitle') || 'Mevcut Durum Değiştirilecek'}
            </DialogTitle>
            <DialogDescription className="space-y-2">
              <p>{t('dailyStatus.overwriteConfirmDesc') || 'Bu hücrede zaten bir durum var. Değiştirmek istiyor musunuz?'}</p>
              {overwriteConfirmDialog.currentStatus && (
                <div className="mt-3 p-3 bg-slate-100 dark:bg-slate-700 rounded-lg">
                  <div className="flex items-center justify-between text-sm">
                    <span>{t('dailyStatus.currentStatus') || 'Mevcut Durum'}:</span>
                    <Badge className={getStatusColor(overwriteConfirmDialog.currentStatus)}>
                      {getStatusEmoji(overwriteConfirmDialog.currentStatus)} {getStatusLabel(overwriteConfirmDialog.currentStatus)}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-center my-2">
                    <ArrowRight className="h-4 w-4 text-slate-400" />
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span>{t('dailyStatus.newStatus') || 'Yeni Durum'}:</span>
                    <Badge className={getStatusColor(overwriteConfirmDialog.newStatus)}>
                      {getStatusEmoji(overwriteConfirmDialog.newStatus)} {getStatusLabel(overwriteConfirmDialog.newStatus)}
                    </Badge>
                  </div>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={handleOverwriteCancel}>
              {t('common.cancel') || 'İptal'}
            </Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={handleOverwriteConfirm}>
              {t('dailyStatus.confirmOverwrite') || 'Evet, Değiştir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Finance Entry Dialog - only visible if canViewFinancial */}
      {canViewFinancial && (
      <Dialog open={financeDialog.open} onOpenChange={(open) => !open && setFinanceDialog({ open: false, userId: null, userName: '' })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Coins className="h-5 w-5 text-amber-600" />
              {t('employeeProfile.addFinanceEntry') || 'Finansal Kayıt Ekle'}
            </DialogTitle>
            <DialogDescription>
              {financeDialog.userName}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('employeeProfile.type') || 'Tür'} *</Label>
              <Select value={financeForm.entry_type} onValueChange={(v) => setFinanceForm({ ...financeForm, entry_type: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="..." />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(FINANCE_TYPE_KEYS).map(([key, { key: i18nKey }]) => (
                    <SelectItem key={key} value={key}>{t(`employeeProfile.${i18nKey}`) || i18nKey}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('employeeProfile.amount') || 'Tutar'} (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={financeForm.amount}
                  onChange={(e) => setFinanceForm({ ...financeForm, amount: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('employeeProfile.date') || 'Tarih'} *</Label>
                <Input
                  type="date"
                  value={financeForm.date}
                  onChange={(e) => setFinanceForm({ ...financeForm, date: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('employeeProfile.description') || 'Açıklama'}</Label>
              <Input
                value={financeForm.description}
                onChange={(e) => setFinanceForm({ ...financeForm, description: e.target.value })}
                placeholder={t('common.optional') || 'Opsiyonel'}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFinanceDialog({ open: false, userId: null, userName: '' })}>
              {t('common.cancel') || 'İptal'}
            </Button>
            <Button onClick={handleAddFinanceEntry} disabled={savingFinance} className="bg-amber-600 hover:bg-amber-700">
              {savingFinance ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              {t('employeeProfile.addNew') || 'Ekle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}

      {/* Note Dialog */}
      <Dialog open={noteDialog.open} onOpenChange={(open) => {
        if (!open) {
          setNoteDialog({ open: false, userId: null, userName: '' });
          setShowPreviousNotes(false);
          setPreviousNotes([]);
        }
      }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-purple-600" />
              Not Ekle
            </DialogTitle>
            <DialogDescription>
              {noteDialog.userName} için not ekleyin (sadece yöneticiler görebilir)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea
              placeholder="Notunuzu buraya yazın..."
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={4}
              className="resize-none"
            />
            
            {/* Toggle Previous Notes Button */}
            <div className="border-t pt-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleTogglePreviousNotes}
                className="w-full justify-between text-slate-600 hover:text-purple-600"
              >
                <span className="flex items-center gap-2">
                  <History className="h-4 w-4" />
                  Bu çalışana ait önceki notları {showPreviousNotes ? 'gizle' : 'göster'}
                </span>
                {showPreviousNotes ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
              
              {/* Previous Notes List */}
              {showPreviousNotes && (
                <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
                  {loadingNotes ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-purple-500" />
                      <span className="ml-2 text-sm text-slate-500">Notlar yükleniyor...</span>
                    </div>
                  ) : previousNotes.length > 0 ? (
                    previousNotes.map((note, idx) => (
                      <div key={note.id || idx} className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border text-sm">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-purple-600 font-medium flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {note.created_at ? new Date(note.created_at).toLocaleDateString('tr-TR', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : '-'}
                          </span>
                          {note.created_by_name && (
                            <span className="text-xs text-slate-400">{note.created_by_name}</span>
                          )}
                        </div>
                        <p className="text-slate-700 dark:text-slate-300">{note.note}</p>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4 text-slate-400 text-sm">
                      Bu çalışana ait not bulunmuyor
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setNoteDialog({ open: false, userId: null, userName: '' });
              setShowPreviousNotes(false);
              setPreviousNotes([]);
            }}>
              İptal
            </Button>
            <Button onClick={handleAddNote} disabled={savingNote || !noteText.trim()} className="bg-purple-600 hover:bg-purple-700">
              {savingNote ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Not Ekle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sync Leaves Dialog */}
      <Dialog open={syncDialog.open} onOpenChange={(open) => !open && setSyncDialog({ open: false, loading: false, data: null })}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="sync-leaves-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCcw className="h-5 w-5 text-orange-600" />
              {t('dailyStatus.syncLeavesTitle') || 'İzin Senkronizasyonu'}
            </DialogTitle>
            <DialogDescription>
              {t('dailyStatus.syncLeavesDesc') || 'Onaylı izin taleplerini çalışma durumuna aktar'}
            </DialogDescription>
          </DialogHeader>
          
          {syncDialog.loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
            </div>
          )}
          
          {syncDialog.data && !syncDialog.loading && (
            <div className="space-y-4">
              {syncDialog.data.total_missing_days === 0 ? (
                <div className="text-center py-6">
                  <ClipboardCheck className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
                  <p className="font-semibold text-emerald-700">{t('dailyStatus.allSynced') || 'Tüm izinler senkronize!'}</p>
                  <p className="text-sm text-slate-500 mt-1">{t('dailyStatus.noMissingDays') || 'Eksik gün bulunamadı.'}</p>
                </div>
              ) : (
                <>
                  <div className="rounded-lg bg-orange-50 border border-orange-200 p-4">
                    <p className="text-sm font-medium text-orange-800">
                      {syncDialog.data.total_users} {t('dailyStatus.syncEmployees') || 'çalışanın'}{' '}
                      <strong>{syncDialog.data.total_missing_days}</strong> {t('dailyStatus.syncMissingDays') || 'eksik günü bulundu'}
                    </p>
                  </div>
                  
                  <div className="max-h-60 overflow-y-auto space-y-2">
                    {syncDialog.data.users.map((u) => {
                      // Group missing days by leave_id
                      const leaveGroups = {};
                      u.missing_days.forEach(d => {
                        const lid = d.leave_id || 'unknown';
                        if (!leaveGroups[lid]) leaveGroups[lid] = [];
                        leaveGroups[lid].push(d);
                      });
                      
                      return (
                      <div key={u.user_id} className="rounded-md border border-slate-200 p-3 hover:bg-slate-50 transition-colors">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm">{u.user_name}</p>
                            <p className="text-xs text-slate-500">{u.missing_days.length} {t('dailyStatus.missingDayCount') || 'eksik gün'}</p>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-xs"
                            onClick={() => handleSyncConfirm([u.user_id])}
                            disabled={syncing}
                          >
                            {syncing ? <Loader2 className="h-3 w-3 animate-spin" /> : t('dailyStatus.syncUser') || 'Senkronize Et'}
                          </Button>
                        </div>
                        {/* Group by leave request */}
                        {Object.entries(leaveGroups).map(([leaveId, days]) => (
                          <div key={leaveId} className="mt-2 p-2 rounded bg-slate-50 border border-slate-100">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex flex-wrap gap-1">
                                {days.map((d, i) => (
                                  <Badge key={i} variant="outline" className="text-xs font-mono">
                                    {d.date} <span className="ml-1 text-slate-400">({d.leave_type})</span>
                                  </Badge>
                                ))}
                              </div>
                              {leaveId !== 'unknown' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 ml-2 shrink-0"
                                  data-testid={`delete-leave-${leaveId}`}
                                  onClick={async () => {
                                    if (!window.confirm('Bu izin talebini silmek istediğinize emin misiniz? Bakiye geri yüklenecektir.')) return;
                                    try {
                                      await api.delete(`/leave-admin/leave-requests/${leaveId}`);
                                      toast.success('İzin talebi silindi');
                                      handleSyncPreview();
                                    } catch (err) {
                                      toast.error(err.response?.data?.detail || 'İzin silinemedi');
                                    }
                                  }}
                                >
                                  <Trash2 className="h-3 w-3 mr-1" />
                                  Sil
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    );})}
                  </div>
                </>
              )}
            </div>
          )}
          
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setSyncDialog({ open: false, loading: false, data: null })}>
              {t('common.close') || 'Kapat'}
            </Button>
            {syncDialog.data && syncDialog.data.total_missing_days > 0 && (
              <Button 
                onClick={() => handleSyncConfirm([])} 
                disabled={syncing}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="sync-all-btn"
              >
                {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCcw className="h-4 w-4 mr-2" />}
                {t('dailyStatus.syncAll') || 'Tümünü Senkronize Et'} ({syncDialog.data.total_missing_days} {t('dailyStatus.day') || 'gün'})
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DailyStatusV2;
